import React, { useEffect, useRef, useState } from "react";
import { BsCopy, BsFillXCircleFill } from "react-icons/bs";
import { RiDeleteBin6Line } from "react-icons/ri";
import { CiTimer } from "react-icons/ci";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import figmaApi from "../api/figmaApi";
import FrameMetadataEditorModal from "../components/modals/FrameMetadataEditorModal";
import PageDeleteConfirmationModal from "../components/modals/PageDeleteConfirmationModal";
import {
  DeleteFrameData,
  FigmaData,
  FigmaDesignIdentifier,
  FrameMetadataUpdate,
} from "../types";
import { DashboardPathParameters } from "../types/path-parameters";
import { uppercaseWords } from "../utils/common-utils";
import { useAuth } from "../hooks/useAuth";
import { checkIsSuperAdmin, canUserEditContent } from "../utils/auth-utils";
import { socket } from '../constants';
import { FaEye } from "react-icons/fa";

/**
 * Displays the list of frames in a single design file.
 * @returns
 */
function SingleDesignView() {
  const { userData } = useAuth();
  const { design_id } = useParams<DashboardPathParameters>();
  const hasFetchedData = useRef(false);
  const isViewer = !canUserEditContent(userData?.role);

  const [data, setData] = useState<Array<FigmaData>>([]);
  const [frameToDelete, setFrameToDelete] = useState<DeleteFrameData | null>(
    null
  );
  const [frameToUpdateMetadata, setFrameToUpdateMetadata] =
    useState<FrameMetadataUpdate | null>(null);
  const [frameToDuplicate, setFrameToDuplicate] = useState<FigmaData | null>(
    null
  );
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isDuplicateLoading, setIsDuplicateLoading] = useState(false);
  const [isFrameMetadataUpdateLoading, setIsFrameMetadataUpdateLoading] =
    useState(false);

  /**
   * Fetch the frames for this design from API & render the list
   * @param data
   */
  const getFramesUnderDesign = async (data: FigmaDesignIdentifier) => {
    try {
      const response = await figmaApi.getFigmaFramesUnderDesign(data);

      if (response.data.statusCode !== 200) {
        throw response.data.message;
      }

      setData(response.data.data);
    } catch (error) {
      console.error(error);
      toast("Did not get any data to display.", { type: "error" });
    }
  };

  // fetch frames under design
  useEffect(() => {
    if (!design_id || hasFetchedData.current) return;
    hasFetchedData.current = true;
    getFramesUnderDesign({ design_id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [design_id]);

  // functions for CTA buttons on each line item --
  /**
   * State handler to open metadata editor modal for a frame.
   * @param frame
   */
  const setDataToUpdateFrameMetadata = (
    frame: FigmaData,
    isForDuplicate = false
  ) => {
    // set context for duplicate
    if (isForDuplicate) {
      setFrameToDuplicate(frame);
    } else {
      setFrameToDuplicate(null);
    }

    setFrameToUpdateMetadata({
      design_id: frame.design_id,
      frame_id: frame.frame_id,
      newName: isForDuplicate
        ? `Duplicate of ${frame.frame_name}`
        : frame.frame_name,
      oldName: frame.frame_name,
    });
  };

  const handleDuplicateClick = (frame: FigmaData) => {
    if (!canUserEditContent(userData?.role)) {
      toast.error("Access denied: Viewer role cannot duplicate content");
      return;
    }
    // open metadata editing modal
    setDataToUpdateFrameMetadata(frame, true);
  };

  /**
   * Execute duplicate frame API call with state data.
   * @param duplicatingFrame
   */
  const duplicateFrame = async (duplicatingFrame: FigmaData) => {
    try {
      setIsDuplicateLoading(true);
      const response = await figmaApi.duplicateFrame({
        design_id: duplicatingFrame.design_id,
        frame_id: duplicatingFrame.frame_id,
        frame_name: `${frameToUpdateMetadata?.newName}`,
      });

      if (response.data.statusCode === 400) {
        throw response.data.message;
      }

      // push the newly created frame into data array
      const duplicateFrameData = response.data.data;
      setData((prev) => [...prev, duplicateFrameData]);
      toast("Page duplicated.", { type: "info" });
    } catch (error) {
      console.error(error);
      toast(
        `Error occurred while making duplicate of ${frameToUpdateMetadata?.newName} `,
        { type: "error" }
      );
    } finally {
      setIsDuplicateLoading(false);
      setFrameToDuplicate(null);
    }
  };

  /**
   * Execute delete frame API call with state data.
   * @param deletePageData
   * @returns
   */
  const deleteFrame = async (deletePageData: DeleteFrameData | null) => {
    if (!deletePageData) return;
    try {
      setIsDeleteLoading(true);
      await figmaApi.deleteFrame(deletePageData);

      setData((prev) => [
        ...prev.filter((item) => item.frame_id !== deletePageData.frame_id),
      ]);

      toast("Page deleted.", { type: "info" });
    } catch (error) {
      toast("Error while deleting page.", { type: "error" });
      console.error(error);
    } finally {
      setIsDeleteLoading(false);
      setFrameToDelete(null);
    }
  };

  /**
   * Execute update frame API call with state data.
   * @param updateData
   * @returns
   */
  const updateFrameMetadata = async (
    updateData: FrameMetadataUpdate | null
  ) => {
    if (!updateData) return;

    try {
      setIsFrameMetadataUpdateLoading(true);
      await figmaApi.updateFigmaFrameMetadata(updateData);
      setData((prev) => [
        ...prev.map((frameData) => {
          // only update the name of the selected item in the datalist
          if (frameData.frame_id === updateData.frame_id) {
            return {
              ...frameData,
              frame_name: updateData.newName,
            };
          } else {
            return frameData;
          }
        }),
      ]);
      toast("Page name updated.", { type: "info" });
    } catch (error) {
      toast("Error while updating page name.", { type: "error" });
      console.error(error);
    } finally {
      setIsFrameMetadataUpdateLoading(false);
    }
  };

  /**
   * Handler for submission of frame name editor modal.
   * Choose the succession call based on duplicate frame or existing frame name update.
   */
  const handleFrameMetadataUpdate = () => {
    if (frameToDuplicate) {
      duplicateFrame(frameToDuplicate);
    } else {
      updateFrameMetadata(frameToUpdateMetadata);
    }
    setFrameToUpdateMetadata(null);
  };

  /**
   * Shows a global page loader upon async tasks.
   */
  const [isSyncLoading, setIsSyncLoading] = useState(false);

  /**
   * Execute sync frame API call with state data.
   */
  const [syncProgress, setSyncProgress] = useState(0);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const finalProgressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    socket.on('syncProgress', (data) => {
      if (data.progress === 20) {
        const startTime = Date.now();
        const duration = 20000; 
        
        progressIntervalRef.current = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(40, 20 + (elapsed / duration) * 20);
          
          if (elapsed >= duration) {
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
            return;
          }
          
          setSyncProgress(progress);
        }, 100);
      } else if (data.progress === 40) {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        setSyncProgress(40);
        
        const startTime = Date.now();
        const duration = 60000; 
        
        finalProgressIntervalRef.current = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(95, 40 + (elapsed / duration) * 55);
          
          if (elapsed >= duration) {
            if (finalProgressIntervalRef.current) {
              clearInterval(finalProgressIntervalRef.current);
              finalProgressIntervalRef.current = null;
            }
            return;
          }
          
          setSyncProgress(progress);
        }, 100);
      }
    });

    socket.on('syncCompleted', (data) => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      if (finalProgressIntervalRef.current) {
        clearInterval(finalProgressIntervalRef.current);
        finalProgressIntervalRef.current = null;
      }
      setSyncProgress(100);
      toast(`${data.message} - ${data.frameName}`, { type: "success" });
      
      setData((prev) => 
        prev.map((item) => 
          item.frame_name === data.frameName 
            ? { ...item, needs_sync: false, is_updated: false }  
            : item
        )
      );
      
      setTimeout(() => {
        setSyncProgress(0);
      }, 1000);
    });
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (finalProgressIntervalRef.current) {
        clearInterval(finalProgressIntervalRef.current);
      }
      socket.off('syncProgress');
      socket.off('syncCompleted');
    };
  }, []);


  const handleSyncFrame = async (frameId: string) => {
    // Security check: Prevent sync for Viewer role
    if (!canUserEditContent(userData?.role)) {
      toast.error("Access denied: Viewer role cannot sync content");
      return;
    }
    
    // Check if design is open before proceeding
    const statusResponse = await figmaApi.checkDesignStatus({ 
      design_id: design_id || '',
      frame_id: frameId 
    });
   
    if (statusResponse.data.statusCode === 400) {
      toast("Error checking design status", { type: "error" });
      return;
    }

    if (!statusResponse.data.data.is_open) {
      toast("Cannot sync when design is not open in Figma.", { type: "error" });
      return;
    }
    else{
      try {
        setIsSyncLoading(true);
        setSyncProgress(0);
        toast("Please Wait! This may take 3-5 mins to complete the sync process.", { type: "info" });
        await figmaApi.updateFrameSync({ frame_id: frameId });
        setData((prev) => 
          prev.map((item) => 
            item.frame_id === frameId 
              ? { ...item, needs_sync: true}  
              : item
          )
        );
      } catch (error) {
        console.error(error);
        toast("Error updating sync status.", { type: "error" });
        setSyncProgress(0);
      } finally {
        setIsSyncLoading(false);
      }
    }
  };
  
  // Update the isLoading check to include sync loading
  const isLoading = isDeleteLoading || isDuplicateLoading || 
    isFrameMetadataUpdateLoading || isSyncLoading;

  const figma_URL = `figma://file/${design_id}`;

  return (
    <div className="page-content-wrapper">
      {isLoading && (
        <div className="loader-overlay">
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}
      {syncProgress > 0 && data.some(item => item.needs_sync) && (
        <div className="sync-progress-container" style={{
          position: 'fixed',
          top: '120px',
          right: '20px',
          width: '300px',
          background: '#f0f0f0',
          borderRadius: '4px',
          padding: '10px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: 1000
        }}>
          <div style={{ marginBottom: '5px' }}>
          <span>Syncing Changes</span>
          <span>&nbsp;&nbsp;{Math.round(syncProgress)}%</span>
          </div>
          <div style={{
            width: '100%',
            height: '10px',
            background: '#ddd',
            borderRadius: '5px',
            overflow: 'hidden',
            zIndex: 1000
          }}>
            <div style={{
              width: `${syncProgress}%`,
              height: '100%',
              background: '#007bff',
              transition: 'width 0.3s ease-in-out'
            }} />
          </div>
        </div>
      )}
      <div className="table-container">
        <div className="text-center">
          {data.length > 0 && (
            <div className="card" style={{ width: "100%", maxWidth: "1200px" }}>
              <div className="card-header">
                <h2 className="card-title">
                  {uppercaseWords(data[0].design_name)}
                </h2>
                <p className="card-description">
                  Manage and view all pages in this design
                </p>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="modern-table">
                    <thead>
                      <tr>
                        <th>Page Name</th>
                        <th>Dimensions</th>
                        {!isViewer && <th>Status</th>}
                        <th>Last Updated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((item) => (
                        <tr key={item.frame_id} className="table-row">
                          <td className="frame-name-cell">
                            <div className="frame-name-wrapper">
                              <span
                                className="frame-name text-truncate"
                                onClick={() => setDataToUpdateFrameMetadata(item)}
                                data-tooltip-id={`name_update_${item._id}`}
                                data-tooltip-content="Click to update name"
                                title={item.frame_name}
                              >
                                {item.frame_name}
                              </span>
                              <a href={`${figma_URL}/?node-id=${encodeURIComponent(item.frame_id)}`}>Link</a>
                              <Tooltip id={`name_update_${item._id}`} />
                            </div>
                          </td>
                          <td className="dimensions-cell">
                            <span className="dimensions">
                              {item.frame_width} × {item.frame_height}
                            </span>
                          </td>
                          {!isViewer && (
                            <td className="status-cell">
                              <div className="status-indicators">
                                {item.is_updated && !item.needs_sync && (
                                  <button
                                    className="btn btn-warning btn-sm sync-btn"
                                    onClick={() => handleSyncFrame(item.frame_id)}
                                    disabled={isSyncLoading}
                                    data-tooltip-id={`sync_${item._id}`}
                                    data-tooltip-content="Sync changes to Figma"
                                  >
                                    <CiTimer className="me-1" />
                                    Sync
                                    <Tooltip id={`sync_${item._id}`} />
                                  </button>
                                )}
                                {item.needs_sync && (
                                  <span className="status-badge syncing">
                                    Syncing...
                                  </span>
                                )}
                                {!item.is_updated && !item.needs_sync && (
                                  <span className="status-badge up-to-date">
                                    Up to date
                                  </span>
                                )}
                              </div>
                            </td>
                          )}
                          <td className="updated-cell">
                            <span className="updated-date">
                              {new Date(item.updated_at).toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                            </span>
                          </td>
                          <td className="actions-cell">
                            <div className="action-buttons">
                              <Link
                                className="btn btn-primary btn-sm"
                                to={`/${item.design_id}/${item.frame_id}`}
                                title="View page"
                              >
                                <FaEye />
                                <Tooltip id={`view_${item._id}`} />
                              </Link>
                              
                              {!isViewer && !item.original_frame_id && (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => handleDuplicateClick(item)}
                                  disabled={isDuplicateLoading}
                                  title="Make duplicate"
                                  data-tooltip-id={`dup_${item._id}`}
                                  data-tooltip-content="Make duplicate"
                                >
                                  <BsCopy />
                                  <Tooltip id={`dup_${item._id}`} />
                                </button>
                              )}
                              
                              {userData && checkIsSuperAdmin(userData?.role) && (
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => {
                                    setFrameToDelete({
                                      design_id: item.design_id,
                                      frame_id: item.frame_id,
                                      frame_name: item.frame_name,
                                      image_url: item.image_url,
                                      is_updated: item.is_updated,
                                    });
                                  }}
                                  disabled={isDeleteLoading}
                                  title="Delete page"
                                  data-tooltip-id={`del_${item._id}`}
                                  data-tooltip-content="Delete page"
                                >
                                  <RiDeleteBin6Line />
                                  <Tooltip id={`del_${item._id}`} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {!!frameToUpdateMetadata && (
        <FrameMetadataEditorModal
          frameUpdate={frameToUpdateMetadata}
          isForDuplicate={!!frameToDuplicate}
          oldName={frameToUpdateMetadata.oldName}
          onStringChange={(newName) => {
            if (!frameToUpdateMetadata) return;
            setFrameToUpdateMetadata((prev) => ({
              design_id: prev!.design_id,
              frame_id: prev!.frame_id,
              oldName: prev!.oldName,
              newName,
            }));
          }}
          onCancel={() => setFrameToUpdateMetadata(null)}
          onSave={handleFrameMetadataUpdate}
        />
      )}
      {!!frameToDelete && (
        <PageDeleteConfirmationModal
          frameName={frameToDelete.frame_name}
          onCancel={() => setFrameToDelete(null)}
          onDelete={() => deleteFrame(frameToDelete)}
          isLoading={isDeleteLoading}
          isUpdatePending={frameToDelete.is_updated}
        />
      )}
    </div>
  );
}

export default SingleDesignView;
